/**
 * Choosing from a list, in a window big enough to read it.
 *
 * The inline autocompletes these replace had the list arrive as a dropdown over
 * a 300px panel: three or four rows visible, each truncated, with the detail
 * that decides the choice — what an entity actually carries, whether any of it
 * collects RF — cut off. Assigning the wrong kit to a movement is not a
 * mistake worth saving a click over.
 *
 * Selection is drafted and applied on save rather than written per click. The
 * assignee and geofence endpoints both replace the whole set, so a five-tick
 * change through an autocomplete was five round trips and five chances to end
 * up with a partial roster.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

const GOLD = '#f5b942';

export interface PickerOption {
  id: string;
  primary: string;
  /** What this is, in a few words. Shown beneath the name. */
  secondary?: string;
  /** A short caveat, shown on the right. */
  note?: string;
  /** Colour the note as a warning rather than an aside. */
  warn?: boolean;
}

/** A second, binary role each chosen row can carry (e.g. convoy vs fixed site). */
export interface PickerRoleToggle {
  /** Ids that start with the role on. Re-read each time the dialog opens. */
  initial: string[];
  /** Chip text while the role is off. Clicking flips it. */
  offLabel: string;
  /** Chip text while the role is on. */
  onLabel: string;
  /** One line on what flipping it means. */
  hint?: string;
}

export interface PickerDialogProps {
  open: boolean;
  title: string;
  /** One line on what the choice does. */
  subtitle?: string;
  options: PickerOption[];
  /** Ids currently chosen. Re-read each time the dialog opens. */
  selected: string[];
  /** Shown when there is nothing to choose from at all. */
  emptyText?: string;
  saving?: boolean;
  /** When set, every chosen row shows a chip that flips its role. */
  roleToggle?: PickerRoleToggle;
  onClose: () => void;
  /** `roleOn` lists the chosen ids whose role chip is on. Empty without `roleToggle`. */
  onSave: (ids: string[], roleOn: string[]) => void;
}

const PickerDialog: React.FC<PickerDialogProps> = ({
  open,
  title,
  subtitle,
  options,
  selected,
  emptyText,
  saving,
  roleToggle,
  onClose,
  onSave,
}) => {
  const [draft, setDraft] = useState<string[]>(selected);
  const [roleOn, setRoleOn] = useState<string[]>(roleToggle?.initial || []);
  const [query, setQuery] = useState('');

  // Reopening after a cancel should show what is actually assigned, not the
  // abandoned edit.
  useEffect(() => {
    if (open) {
      setDraft(selected);
      setRoleOn(roleToggle?.initial || []);
      setQuery('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((o) => (
      o.primary.toLowerCase().includes(needle)
      || (o.secondary || '').toLowerCase().includes(needle)
    ));
  }, [options, query]);

  const toggle = (id: string) => setDraft((cur) => (
    cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]
  ));

  const flipRole = (id: string) => setRoleOn((cur) => (
    cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]
  ));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.25 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{title}</Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </Box>
      <Divider />
      <DialogContent sx={{ pt: 1.5, pb: 1 }}>
        {options.length > 8 && (
          <TextField
            autoFocus
            fullWidth
            size="small"
            variant="standard"
            placeholder="Search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ fontSize: 16, mr: 0.75, color: 'text.disabled' }} />
              ),
            }}
            sx={{ mb: 1 }}
          />
        )}

        {options.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', py: 1 }}>
            {emptyText || 'Nothing to choose from yet.'}
          </Typography>
        )}

        {options.length > 0 && shown.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', py: 1 }}>
            Nothing matches “{query}”.
          </Typography>
        )}

        <Box sx={{ maxHeight: 320, overflow: 'auto', mx: -1 }}>
          {shown.map((option) => {
            const checked = draft.includes(option.id);
            return (
              <Box
                key={option.id}
                role="button"
                tabIndex={0}
                onClick={() => toggle(option.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(option.id); }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 0.25,
                  borderRadius: 0.75,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                  '&:focus-visible': { outline: `1px solid ${GOLD}`, outlineOffset: -1 },
                }}
              >
                <Checkbox
                  checked={checked}
                  tabIndex={-1}
                  size="small"
                  sx={{ p: 0.5, color: 'text.disabled', '&.Mui-checked': { color: GOLD } }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap sx={{ fontWeight: checked ? 700 : 400 }}>
                    {option.primary}
                  </Typography>
                  {option.secondary && (
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                      {option.secondary}
                    </Typography>
                  )}
                </Box>
                {option.note && (
                  <Typography
                    variant="caption"
                    sx={{ flexShrink: 0, color: option.warn ? '#ef9a9a' : 'text.secondary' }}
                  >
                    {option.note}
                  </Typography>
                )}
                {roleToggle && checked && (
                  <Typography
                    variant="caption"
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); flipRole(option.id); }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        flipRole(option.id);
                      }
                    }}
                    sx={{
                      flexShrink: 0,
                      px: 0.75,
                      py: 0.1,
                      borderRadius: 2,
                      fontWeight: 700,
                      fontSize: '0.62rem',
                      cursor: 'pointer',
                      userSelect: 'none',
                      color: roleOn.includes(option.id) ? '#4dd0e1' : 'text.secondary',
                      border: '1px solid',
                      borderColor: roleOn.includes(option.id)
                        ? 'rgba(77, 208, 225, 0.55)'
                        : 'rgba(255,255,255,0.18)',
                      '&:hover': { borderColor: roleOn.includes(option.id) ? '#4dd0e1' : 'text.secondary' },
                      '&:focus-visible': { outline: `1px solid ${GOLD}`, outlineOffset: 1 },
                    }}
                  >
                    {roleOn.includes(option.id) ? roleToggle.onLabel : roleToggle.offLabel}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>

        {roleToggle?.hint && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pt: 0.75 }}>
            {roleToggle.hint}
          </Typography>
        )}

        <Divider sx={{ mt: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pt: 1.25 }}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            {draft.length} selected
          </Typography>
          <Typography
            variant="caption"
            onClick={onClose}
            sx={{ fontWeight: 700, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          >
            Cancel
          </Typography>
          <Typography
            variant="caption"
            onClick={saving ? undefined : () => onSave(draft, roleOn.filter((id) => draft.includes(id)))}
            sx={{
              fontWeight: 700,
              cursor: saving ? 'default' : 'pointer',
              color: saving ? 'text.disabled' : GOLD,
              '&:hover': saving ? undefined : { textDecoration: 'underline' },
            }}
          >
            Save
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PickerDialog;
